import React, { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

const images = [
  { src: "/Images/ADU and Custom Homes/ADU and Custom Homes 1.jpeg"},
  { src: "/Images/ADU and Custom Homes/ADU and Custom Homes 2.jpeg"},
  { src:"/Images/ADU and Custom Homes/ADU and Custom Homes 3.jpeg" },
  { src:"/Images/ADU and Custom Homes/ADU and Custom Homes 4.jpeg"},
  { src: "/Images/ADU and Custom Homes/ADU and Custom Homes 5.jpeg"},
  { src:"/Images/ADU and Custom Homes/ADU and Custom Homes 6.jpeg" },
  { src:"/Images/ADU and Custom Homes/ADU and Custom Homes 7.jpeg"},
];

const GAP = 16;
const COLS = 3;
const TOTAL = images.length;
const START = TOTAL;

const looped = [...images, ...images, ...images];

const ADUPortfolio = () => {
  const [lightbox, setLightbox] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const [index, setIndex] = useState(START);
  const [transitioning, setTransitioning] = useState(true);

  const containerRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        const cols = w < 640 ? 1 : w < 900 ? 2 : COLS;
        setCardWidth((w - GAP * (cols - 1)) / cols);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!transitioning) return;
    if (index >= TOTAL * 2 || index <= 0) {
      const timer = setTimeout(() => {
        setTransitioning(false);
        const clamped = ((index - TOTAL) % TOTAL + TOTAL) % TOTAL + TOTAL;
        setIndex(clamped);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setTransitioning(true));
        });
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [index, transitioning]);

  const next = useCallback(() => setIndex((p) => p + COLS), []);
  const prev = useCallback(() => setIndex((p) => p - COLS), []);

  useEffect(() => {
    if (isHovered) return;
    const id = setInterval(next, 4000);
    return () => clearInterval(id);
  }, [isHovered, next]);

  const dotIndex = Math.floor(((index - START) % TOTAL + TOTAL) % TOTAL / COLS);
  const totalPages = Math.ceil(TOTAL / COLS);

  return (
    <>
      <section
        className="bg-white py-12 px-6"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
          
            <h2 className="text-2xl md:text-3xl font-bold text-[#212529]">
              ADU and Custom Homes Project Gallery
            </h2>
            <p className="text-[#6c757d] text-sm mt-2 max-w-xl mx-auto">
              A showcase of our accessory dwelling units, garage conversions, and detached unit builds.
            </p>
          </motion.div>

          {/* Slider Track */}
          <div ref={containerRef} className="overflow-hidden rounded-2xl">
            <div
              ref={trackRef}
              style={{
                display: "flex",
                gap: `${GAP}px`,
                transform: cardWidth ? `translateX(-${(index / COLS) * (cardWidth * COLS + GAP * COLS)}px)` : "none",
                transition: transitioning ? "transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)" : "none",
                willChange: "transform",
              }}
            >
              {looped.map((img, i) => (
                <div
                  key={i}
                  style={{ flex: `0 0 ${cardWidth}px`, width: cardWidth }}
                  className="relative overflow-hidden rounded-2xl group cursor-pointer aspect-[4/3] flex-shrink-0"
                  onClick={() => setLightbox(img)}
                >
                  <img
                    src={img.src}
                    alt={img.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-4">
                    <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                      {img.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#f1f3f5] hover:bg-[#212529] hover:text-white text-[#212529] text-lg font-bold transition-all duration-200"
            >
              ‹
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setIndex(START + idx * COLS)}
                  className={`rounded-full transition-all duration-300 ${
                    dotIndex === idx
                      ? "bg-[#212529] w-5 h-1.5"
                      : "bg-[#dee2e6] hover:bg-[#adb5bd] w-1.5 h-1.5"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#f1f3f5] hover:bg-[#212529] hover:text-white text-[#212529] text-lg font-bold transition-all duration-200"
            >
              ›
            </button>
          </div>

        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.img
              src={lightbox.src}
              alt={lightbox.label}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <p className="text-white/70 text-sm mt-4 text-center max-w-lg">
              {lightbox.label}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ADUPortfolio;